import uPlot from 'uplot'

import { RelativeScale } from './scale'
import { uPlotTooltipPlugin } from './plugins'
import { formatNumber, formatTimestampSeconds } from './util'

export class ServerDetailView {
  constructor (app) {
    this._app = app
    this._plotInstance = null
    this._visible = false
    this._comparisonServerRegistration = null
  }

  show (serverRegistration) {
    this._serverRegistration = serverRegistration
    const data = serverRegistration.data
    const graphData = serverRegistration._graphData
    const playerCounts = graphData[1]

    // Compute stats
    const validCounts = playerCounts.filter(v => v !== null && typeof v === 'number')
    const currentPlayers = serverRegistration.playerCount
    const peak24h = serverRegistration.lastPeakData ? serverRegistration.lastPeakData.playerCount : (validCounts.length > 0 ? Math.max(...validCounts) : 0)
    const avg24h = validCounts.length > 0 ? Math.round(validCounts.reduce((s, v) => s + v, 0) / validCounts.length) : 0
    const allTimePeak = serverRegistration.lastRecordData ? serverRegistration.lastRecordData.playerCount : peak24h

    const isOnline = typeof serverRegistration.playerCount === 'number' && serverRegistration.playerCount > 0
    const faviconEl = document.getElementById(`favicon_${serverRegistration.serverId}`)
    const faviconSrc = faviconEl ? faviconEl.src : ''

    const container = document.getElementById('server-detail')
    container.innerHTML = `
      <div class="server-detail-inner">
        <button class="server-detail-back" id="server-detail-back">
          <svg viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          Back to Tracker
        </button>
        <div class="server-detail-header">
          <img class="server-detail-favicon" src="${faviconSrc}" alt="${data.name}">
          <div class="server-detail-header-info">
            <h2 class="server-detail-name">${data.name}</h2>
            <span class="server-detail-address">${data.ip}${data.port && data.port !== 25565 ? ':' + data.port : ''}</span>
          </div>
          <span class="server-detail-badge ${isOnline ? 'online' : 'offline'}">${isOnline ? 'Online' : 'Offline'}</span>
        </div>
        <div class="server-detail-graph-card">
          <div class="server-detail-graph-header">
            <h3>Player History</h3>
            <select id="server-compare-select" class="server-compare-select">
              <option value="">Compare with...</option>
            </select>
          </div>
          <div id="server-detail-graph"></div>
        </div>
        <div class="server-detail-stats" id="server-detail-stats-primary">
          <div class="server-detail-stat">
            <div class="server-detail-stat-value">${formatNumber(currentPlayers)}</div>
            <div class="server-detail-stat-label">Current Players</div>
          </div>
          <div class="server-detail-stat">
            <div class="server-detail-stat-value">${formatNumber(peak24h)}</div>
            <div class="server-detail-stat-label">24h Peak</div>
          </div>
          <div class="server-detail-stat">
            <div class="server-detail-stat-value">${formatNumber(avg24h)}</div>
            <div class="server-detail-stat-label">24h Average</div>
          </div>
          <div class="server-detail-stat">
            <div class="server-detail-stat-value">${formatNumber(allTimePeak)}</div>
            <div class="server-detail-stat-label">All-Time Peak</div>
          </div>
        </div>
        <div id="server-detail-comparison-stats"></div>
      </div>
    `

    // Populate comparison dropdown
    const select = document.getElementById('server-compare-select')
    for (const reg of this._app.serverRegistry.getServerRegistrations()) {
      if (reg.serverId === serverRegistration.serverId) continue
      const option = document.createElement('option')
      option.value = reg.serverId
      option.textContent = reg.data.name
      select.appendChild(option)
    }

    select.addEventListener('change', () => {
      const selectedValue = select.value
      if (!selectedValue) {
        this._comparisonServerRegistration = null
        this._buildGraph(graphData)
        this._clearComparisonStats()
      } else {
        const comparisonReg = this._app.serverRegistry.getServerRegistration(parseInt(selectedValue))
        this._comparisonServerRegistration = comparisonReg
        this._buildComparisonGraph(graphData, comparisonReg)
        this._updateComparisonStats(comparisonReg)
      }
    })

    // Hide main content and footer, show detail view
    document.getElementById('push').style.display = 'none'
    document.getElementById('footer').style.display = 'none'
    container.style.display = 'block'

    // Build uPlot graph
    this._buildGraph(graphData)

    // Bind back button
    document.getElementById('server-detail-back').addEventListener('click', () => this.hide())

    this._visible = true

    // Scroll to top
    window.scrollTo(0, 0)
  }

  _buildGraph (graphData) {
    if (this._plotInstance) {
      this._plotInstance.destroy()
      this._plotInstance = null
    }

    const graphContainer = document.getElementById('server-detail-graph')
    const width = graphContainer.offsetWidth || 800

    const tickCount = 6

    // eslint-disable-next-line new-cap
    this._plotInstance = new uPlot({
      plugins: [
        uPlotTooltipPlugin((pos, id) => {
          if (pos) {
            const playerCount = graphData[1][id]
            if (typeof playerCount !== 'number') {
              this._app.tooltip.hide()
            } else {
              this._app.tooltip.set(pos.left, pos.top, 10, 10, `${formatNumber(playerCount)} Players<br>${formatTimestampSeconds(graphData[0][id])}`)
            }
          } else {
            this._app.tooltip.hide()
          }
        })
      ],
      height: 300,
      width: width,
      cursor: {
        y: false,
        drag: {
          x: true,
          y: false
        }
      },
      series: [
        {},
        {
          stroke: '#3b82f6',
          fill: 'rgba(59, 130, 246, 0.1)',
          width: 2,
          value: (_, raw) => `${formatNumber(raw)} Players`,
          spanGaps: true,
          points: {
            show: false
          }
        }
      ],
      axes: [
        {
          stroke: '#788798',
          grid: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          },
          font: '12px "Inter", sans-serif',
          space: 80,
          ticks: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          }
        },
        {
          stroke: '#788798',
          font: '12px "Inter", sans-serif',
          size: 60,
          grid: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          },
          ticks: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          },
          split: () => {
            const { scaledMin, scaledMax, scale } = RelativeScale.scale(graphData[1], tickCount)
            return RelativeScale.generateTicks(scaledMin, scaledMax, scale)
          }
        }
      ],
      scales: {
        y: {
          auto: false,
          range: () => {
            const { scaledMin, scaledMax } = RelativeScale.scale(graphData[1], tickCount)
            return [scaledMin, scaledMax]
          }
        }
      },
      legend: {
        show: false
      }
    }, graphData, graphContainer)
  }

  _buildComparisonGraph (primaryGraphData, comparisonRegistration) {
    if (this._plotInstance) {
      this._plotInstance.destroy()
      this._plotInstance = null
    }

    const graphContainer = document.getElementById('server-detail-graph')
    const width = graphContainer.offsetWidth || 800

    const tickCount = 6
    const maxFactor = 4

    const timestamps = primaryGraphData[0]
    const primaryCounts = primaryGraphData[1]
    const comparisonCounts = comparisonRegistration._graphData[1]

    // Determine colors
    const primaryColor = this._serverRegistration.data.color || '#3b82f6'
    let comparisonColor = comparisonRegistration.data.color || '#f59e0b'
    if (comparisonColor === primaryColor) {
      comparisonColor = '#f59e0b'
    }

    const plotData = [timestamps, primaryCounts, comparisonCounts]
    const primaryName = this._serverRegistration.data.name
    const comparisonName = comparisonRegistration.data.name

    // eslint-disable-next-line new-cap
    this._plotInstance = new uPlot({
      plugins: [
        uPlotTooltipPlugin((pos, idx) => {
          if (pos) {
            const pCount = primaryCounts[idx]
            const cCount = comparisonCounts[idx]
            let text = ''
            if (typeof pCount === 'number') {
              text += `<strong>${primaryName}</strong>: ${formatNumber(pCount)}`
            }
            if (typeof cCount === 'number') {
              if (text) text += '<br>'
              text += `<strong style="color:${comparisonColor}">${comparisonName}</strong>: ${formatNumber(cCount)}`
            }
            if (text) {
              text += `<br><br><strong>${formatTimestampSeconds(timestamps[idx])}</strong>`
              this._app.tooltip.set(pos.left, pos.top, 10, 10, text)
            } else {
              this._app.tooltip.hide()
            }
          } else {
            this._app.tooltip.hide()
          }
        })
      ],
      height: 300,
      width: width,
      cursor: {
        y: false,
        drag: {
          x: true,
          y: false
        }
      },
      series: [
        {},
        {
          label: primaryName,
          stroke: primaryColor,
          fill: primaryColor + '1a',
          width: 2,
          value: (_, raw) => `${formatNumber(raw)} Players`,
          spanGaps: true,
          points: { show: false }
        },
        {
          label: comparisonName,
          stroke: comparisonColor,
          fill: comparisonColor + '1a',
          width: 2,
          value: (_, raw) => `${formatNumber(raw)} Players`,
          spanGaps: true,
          points: { show: false }
        }
      ],
      axes: [
        {
          stroke: '#788798',
          grid: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          },
          font: '12px "Inter", sans-serif',
          space: 80,
          ticks: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          }
        },
        {
          stroke: '#788798',
          font: '12px "Inter", sans-serif',
          size: 60,
          grid: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          },
          ticks: {
            stroke: 'rgba(100, 116, 139, 0.15)',
            width: 1
          },
          split: () => {
            const { scaledMax, scale } = RelativeScale.scaleMatrix([primaryCounts, comparisonCounts], tickCount, maxFactor)
            return RelativeScale.generateTicks(0, scaledMax, scale)
          }
        }
      ],
      scales: {
        y: {
          auto: false,
          range: () => {
            const { scaledMin, scaledMax } = RelativeScale.scaleMatrix([primaryCounts, comparisonCounts], tickCount, maxFactor)
            return [scaledMin, scaledMax]
          }
        }
      },
      legend: {
        show: false
      }
    }, plotData, graphContainer)
  }

  _updateComparisonStats (comparisonRegistration) {
    const compCounts = comparisonRegistration._graphData[1]
    const validCounts = compCounts.filter(v => v !== null && typeof v === 'number')
    const currentPlayers = comparisonRegistration.playerCount
    const peak24h = comparisonRegistration.lastPeakData ? comparisonRegistration.lastPeakData.playerCount : (validCounts.length > 0 ? Math.max(...validCounts) : 0)
    const avg24h = validCounts.length > 0 ? Math.round(validCounts.reduce((s, v) => s + v, 0) / validCounts.length) : 0
    const allTimePeak = comparisonRegistration.lastRecordData ? comparisonRegistration.lastRecordData.playerCount : peak24h

    const container = document.getElementById('server-detail-comparison-stats')
    container.innerHTML = `
      <div class="server-detail-comparison-stats">
        <div class="server-detail-stats">
          <div class="server-detail-stat comparison-stat">
            <div class="server-detail-stat-value comparison-value">${formatNumber(currentPlayers)}</div>
            <div class="server-detail-stat-label">${comparisonRegistration.data.name} Current</div>
          </div>
          <div class="server-detail-stat comparison-stat">
            <div class="server-detail-stat-value comparison-value">${formatNumber(peak24h)}</div>
            <div class="server-detail-stat-label">${comparisonRegistration.data.name} 24h Peak</div>
          </div>
          <div class="server-detail-stat comparison-stat">
            <div class="server-detail-stat-value comparison-value">${formatNumber(avg24h)}</div>
            <div class="server-detail-stat-label">${comparisonRegistration.data.name} 24h Avg</div>
          </div>
          <div class="server-detail-stat comparison-stat">
            <div class="server-detail-stat-value comparison-value">${formatNumber(allTimePeak)}</div>
            <div class="server-detail-stat-label">${comparisonRegistration.data.name} All-Time Peak</div>
          </div>
        </div>
      </div>
    `
  }

  _clearComparisonStats () {
    const container = document.getElementById('server-detail-comparison-stats')
    if (container) {
      container.innerHTML = ''
    }
  }

  hide () {
    if (this._plotInstance) {
      this._plotInstance.destroy()
      this._plotInstance = null
    }

    const container = document.getElementById('server-detail')
    container.style.display = 'none'
    container.innerHTML = ''

    document.getElementById('push').style.display = 'block'
    document.getElementById('footer').style.display = 'block'

    this._visible = false
    this._serverRegistration = null
    this._comparisonServerRegistration = null
  }

  get visible () {
    return this._visible
  }
}
